import React, { useEffect, useCallback, useState, memo } from 'react';
import { flushSync } from 'react-dom';
import StillageHistoryModal from './StillageHistoryModal';
import { getStillageHistoryDetails } from 'services/stillageDashboard';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import StillageDataTable from './StillageDataTable';
import LostStillageModal from './LostStillageModal';

const StillageTable = ({ data = [], loading, editingTypeStillageNo, setEditingTypeStillageNo, onChangeType, onUpdateStillage }) => {
    const [showModal, setShowModal] = useState(false);
    const [showLostModal, setShowLostModal] = useState(false);
    const [lostModalData, setLostModalData] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedStillage, setSelectedStillage] = useState(null);

    useEffect(() => {
        // Close the type select if clicking outside
        const handleDocumentClick = e => {
            if (!e.target.closest('.stillage-type-select'))
                setEditingTypeStillageNo(null);
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleClickRow = useCallback((e, row) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent?.stopImmediatePropagation)
            e.nativeEvent.stopImmediatePropagation();

        const { original: st } = row;
        // find the clicked cell element and its index in the row
        const td = e?.target?.closest && e.target.closest('td');
        const tr = e?.currentTarget || e?.target?.closest && e.target.closest('tr');
        const cellIndex = (td && tr) ? Array.from(tr.children).indexOf(td) : -1;

        // // react-table keeps cells on the row in order
        const cell = (row?.cells && cellIndex >= 0) ? row.cells[cellIndex] : undefined;
        const { column: { Header } } = cell;
        if (Header === 'Type') {
            // Allow users to amend the stillage type without opening the modal
            setEditingTypeStillageNo(st.stillageNo);
            return;
        }
        if (Header === 'Lost?') {
            // Open modal to mark stillage as lost
            setShowLostModal(true);
            setLostModalData(st);
            return;
        }
        setLostModalData(null);
        setEditingTypeStillageNo(null);
        // Imediately update state to show modal
        flushSync(() => {
            setSelectedStillage(st.stillageNo);
            setModalData(null);
            setModalLoading(true);
            setShowModal(true);
        });

        setTimeout(() => {
            getStillageHistoryDetails(st.stillageNo)
                .then(data => {
                    setModalData(data);
                })
                .catch(error => {
                    toast.error('Error fetching history data:', error);
                    setModalData(null);
                    setShowModal(false);
                })
                .finally(() => setModalLoading(false))
        }, 0);
    }, []);

    return (
        <>
            <StillageDataTable
                data={data}
                loading={loading}
                handleClickRow={handleClickRow}
                editingTypeStillageNo={editingTypeStillageNo}
                onChangeType={onChangeType}
            />
            <StillageHistoryModal
                showModal={showModal}
                setShowModal={setShowModal}
                stillageNo={selectedStillage || null}
                modalData={modalData}
                loading={modalLoading}
            />
            <LostStillageModal modalData={lostModalData} showModal={showLostModal} setShowModal={setShowLostModal} onUpdateStillage={onUpdateStillage} />
        </>
    );
}

StillageTable.propTypes = {
    data: PropTypes.array,
    loading: PropTypes.bool,
    editingTypeStillageNo: PropTypes.number,
    setEditingTypeStillageNo: PropTypes.func,
    onChangeType: PropTypes.func,
    onUpdateStillage: PropTypes.func,
};

export default memo(StillageTable);